import { useCallback } from 'react';
import { BusinessStatus } from '../../../mappers/business/toBusinessModel';
import {
    hasAccessToBusinessStat,
    isBloggerBusiness,
    canEditBusinessAvatar,
} from '../../../utils/accessService';
import { cropPublicId } from '../../../utils/cropPublicId';
import { formatText } from '../../../utils/format/formatText';
import { ymTrackStatisticsClick } from '../../../utils/metrika';
import { Flex } from '../../layout/Flex';
import { Image } from '../../media/Image';
import { MediaCover } from '../../media/MediaCover';
import { TypographyExpander } from '../../typography/TypographyExpander';
import { BlockedStatus } from '../BlockedStatus';
import { DescriptionStatus } from '../BusinessCard/parts/DescriptionStatus';
import { BusinessStatistic } from '../BusinessStatistic/BusinessStatistic';
import { BusinessType } from '../types';
import { BUSINESS_COVER_RATIO, BUSINESS_COVER_WIDTH, DEFAULT_COVER_ID } from './constants';
import { ProfileActions } from './parts/ProfileActions/ProfileActions';
import { ProfileAvatar } from './parts/ProfileAvatar/ProfileAvatar';
import { ProfileInfo } from './parts/ProfileInfo/ProfileInfo';
import { ProfileSite } from './parts/ProfileSite/ProfileSite';
import { ProfileStat } from './parts/ProfileStat/ProfileStat';
import { Wrapper } from './styles';
import { BusinessProfileProps } from './types';

export const BusinessProfile = ({
    business,
    user,
    businessStatistic,
    statisticLoading,
    children,
    cpaButton,
    removeMenuItem,
    onBadgeClick,
    onSubscribe,
    onCatalogClick,
    onPersistBusinessAvatar,
    onPersistBusinessCover,
    onReport,
    onShare: onBaseShare,
    ...other
}: BusinessProfileProps) => {
    const isBlogger = isBloggerBusiness(business.type);
    const canEditBusiness = canEditBusinessAvatar(business.id, user);

    const title = isBlogger ? business.displayName : cropPublicId(business.publicId);
    const subtitle = isBlogger ? undefined : business.displayName;

    const coverUrl =
        business.type !== BusinessType.BUSINESS || business.coverUrl?.includes(DEFAULT_COVER_ID)
            ? undefined
            : business.coverUrl;

    const onAllStatisticClick = useCallback(() => {
        ymTrackStatisticsClick({
            page: 'Профиль бизнеса',
            userLogin: user?.login,
            businessId: user?.profile?.publicId,
        });
    }, [user?.login, user?.profile?.publicId]);

    const onShare = useCallback(() => onBaseShare(business), [business, onBaseShare]);

    return (
        <Wrapper {...other}>
            <Flex
                direction="row"
                spacing={9}
                width="100%"
                justifyContent="space-between"
                overflow="hidden"
            >
                <Flex spacing={2} flex={1}>
                    <Flex direction="row" spacing={2}>
                        <ProfileAvatar
                            avatarUrl={business.avatarUrl}
                            title={business.displayName}
                            canEditBusiness={canEditBusiness}
                            onPersistBusinessAvatar={onPersistBusinessAvatar}
                        />
                        <ProfileInfo
                            title={title}
                            subtitle={subtitle}
                            business={business}
                            user={user}
                            cpaButton={cpaButton}
                            removeMenuItem={removeMenuItem}
                            onBadgeClick={onBadgeClick}
                            onReport={onReport}
                            onPersistBusinessCover={onPersistBusinessCover}
                        />
                    </Flex>
                    <ProfileStat business={business} user={user} />
                    {business.status === BusinessStatus.DECLINED && (
                        <BlockedStatus px={0} py={0} login={user?.login} isBlogger={isBlogger} />
                    )}
                    {business.description ? (
                        <Flex>
                            {business.descriptionStatus ? (
                                <DescriptionStatus mt={0} status={business.descriptionStatus} />
                            ) : null}
                            <TypographyExpander
                                width="100%"
                                color="primary"
                                lines={3}
                                backgroundColor="var(--background-primary)"
                            >
                                {formatText(business.description)}
                            </TypographyExpander>
                        </Flex>
                    ) : null}
                    <ProfileSite business={business} />
                    <ProfileActions
                        business={business}
                        user={user}
                        onSubscribe={onSubscribe}
                        onShare={onShare}
                        onCatalogClick={onCatalogClick}
                    />
                </Flex>
                {coverUrl ? (
                    <Flex maxWidth="50%" borderRadius={2} overflow="hidden">
                        <MediaCover
                            ratio={BUSINESS_COVER_RATIO}
                            width={BUSINESS_COVER_WIDTH}
                            maxWidth="100%"
                            overflow="hidden"
                        >
                            <Image src={coverUrl} />
                        </MediaCover>
                    </Flex>
                ) : null}
            </Flex>
            {hasAccessToBusinessStat(business.id, user) && (
                <BusinessStatistic
                    borderTop="1px solid var(--divider)"
                    width="100%"
                    px={0}
                    py={0}
                    pb={0}
                    statistic={businessStatistic?.stats}
                    statisticUrl={business.statisticUrl}
                    onAllStatisticClick={onAllStatisticClick}
                    statisticLoading={statisticLoading}
                    isService={business?.isService}
                />
            )}
            {children}
        </Wrapper>
    );
};
